
import { useMemo } from "react";
import { CardHeaderTag } from "components/shared/Tags/StatusTags";
import { getPoStatusDisplayConfig } from "../poUtils";
import { CalendarDays, IndianRupee, MapPin, Download } from "lucide-react";
import { strings } from "utils/constants/strings";
import Table from "components/shared/Table/Table";
import Breadcrumb from "components/shared/Breadcrumb/Breadcrumb";
import { Loader } from "../../Common/Loader";
import { Button, FlexColumn, SushiModal } from "../../Common/styles.common";
import Input from "components/shared/InputTypes/Input";
import {
  DesktopPage,
  TopRow,
  TitleBlock,
  Title,
  Actions,
  ActionButton,
  SummaryCard,
  SummaryItem,
  SummaryIcon,
  SummaryValue,
  SummaryText,
  SummaryLabel,
  ProductsCard,
  ProductsTitle,
  ProductImage,
  CellText,
  StateMessage,
  DetailsSummaryCard,
} from "./styles.DDPoDetailsDesktop";

export default function DDPoDetailsDesktop({
  po,
  grn,
  items,
  showLoader,
  tonnage,
  popupText,
  showPopup,
  showInvoiceNumberModal,
  setShowInvoiceNumberModal,
  overriddenInvoiceNumber,
  navigateToInvoiceForm,
  navigateToBuyerOrdersList,
  handleGenerateEwb,
  togglePopupModel,
  downloadDeliveryInvoices,
  clickInsertInvoiceNumber,
  generateInvoiceCTA,
  handleInvoiceNumberChange,
  shouldShowEwbButton,
  navigate,
  purchaseOrderId,
  getActionBarCTA,
  goToDDList,
}) {
  const columns = useMemo(() => {
    const unit = items?.[0]?.buy_measurement_unit || "unit";
    return [
      {
        id: "image",
        header: "Product image",
        meta: { width: 100 },
        cell: (info) => (
          <ProductImage src={info.row.original.product_image} alt="" />
        ),
      },
      {
        id: "name",
        header: "Product name",
        meta: { width: 280 },
        cell: (info) => <CellText>{info.row.original.product_name}</CellText>,
      },
      {
        id: "price",
        header: `Price/${unit}`,
        meta: { width: 160 },
        cell: (info) => (
          <CellText>
            &#8377;{" "}
            {Number(info.row.original.price_per_unit || 0).toLocaleString("en-IN")}/
            {info.row.original.buy_measurement_unit}
          </CellText>
        ),
      },
      {
        id: "qty",
        header: "Order qty",
        meta: { width: 140 },
        cell: (info) => (
          <CellText>
            {Number(info.row.original.quantity_ordered || 0).toLocaleString("en-IN")}{" "}
            {info.row.original.buy_measurement_unit}
          </CellText>
        ),
      },
      {
        id: "amount",
        header: "Order amount",
        cell: (info) => (
          <CellText>
            &#8377;{" "}
            {(
              Number(info.row.original.quantity_ordered || 0) *
              Number(info.row.original.price_per_unit || 0)
            ).toLocaleString("en-IN")}
          </CellText>
        ),
      },
    ];
  }, [items]);

  if (showLoader) return <Loader />;
  if (!po) return <StateMessage>Could not load this purchase order.</StateMessage>;

  const cta = getActionBarCTA?.();

  // Resolve status display
  const poStatus = po.po_status;
  const { label: statusLabel, bgColor, textColor } = getPoStatusDisplayConfig(poStatus || "");

  return (
    <DesktopPage>
      {/* ── Invoice number modal ── */}
      {showInvoiceNumberModal && (
        <SushiModal
          visible={showInvoiceNumberModal}
          isCentered
          title={"Enter Invoice Number"}
          showCloseIcon={true}
          onClose={() => setShowInvoiceNumberModal(false)}
        >
          <FlexColumn className="align-items-center invoice-modal w-full">
            <span className="wd-100">
              <Input
                type="text"
                placeholder="Invoice number"
                value={overriddenInvoiceNumber}
                onChange={(value) => handleInvoiceNumberChange(value)}
                className="w-full  border-redius-12   input-class"
              />
              {" "}
            </span>
            <Button
              className="fs-13 text-white fw-600 border-redius-12"
              onClick={generateInvoiceCTA}
            >
              Click to Generate Invoice
            </Button>
          </FlexColumn>
        </SushiModal>
      )}

      {/* ── Info popup ── */}
      <SushiModal visible={showPopup} isCentered>
        <FlexColumn className="align-items-center p-5">
          <img src={"/logos/info_logo.svg"} height="80px" width="80px" />
          <span className="fs-22 fw-700 my-4">{popupText}</span>
          <span
            className="fs-14 fw-500 text-red-500"
            style={{ cursor: "pointer" }}
            onClick={togglePopupModel}
          >
            {" "}
            Okay
          </span>
        </FlexColumn>
      </SushiModal>

      {/* ── TopRow: breadcrumb + title + status + action bar ── */}
      <TopRow>
        <TitleBlock>
          <Breadcrumb
            items={[
              { label: "Home" },
              { label: "Direct Delivery", onClick: goToDDList },
              { label: po.purchase_order_number },
            ]}
          />
          <TopRow>
            <Title>{po.purchase_order_number}</Title>
            {poStatus && (
              <CardHeaderTag
                text={statusLabel}
                textColor={textColor}
                bgColor={bgColor}
                allSidesRounded
              />
            )}
          </TopRow>
        </TitleBlock>
        <Actions>
          <ActionButton type="button" onClick={goToDDList}>
            Back
          </ActionButton>
          {cta && (
            <ActionButton type="button" $primary onClick={cta.onClick}>
              {cta.label}
            </ActionButton>
          )}
        </Actions>
      </TopRow>

      {/* ── Summary card ── */}
      <DetailsSummaryCard>
        <SummaryItem>
          <SummaryIcon><CalendarDays size={20} /></SummaryIcon>
          <SummaryText>
            <SummaryLabel>Deliver by</SummaryLabel>
            <SummaryValue>
              {po.expected_delivery_date
                ? new Date(po.expected_delivery_date).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : "—"}
            </SummaryValue>
          </SummaryText>
        </SummaryItem>

        {po.order_tag === "DIRECT_DELIVERY" && po.buyer_outlet_details?.name?.length > 0 && (
          <SummaryItem>
            <SummaryIcon><MapPin size={20} /></SummaryIcon>
            <SummaryText>
              <SummaryLabel>Deliver at</SummaryLabel>
              <SummaryValue>{po.buyer_outlet_details.name}</SummaryValue>
            </SummaryText>
          </SummaryItem>
        )}

        <SummaryItem>
          <SummaryIcon><IndianRupee size={20} /></SummaryIcon>
          <SummaryText>
            <SummaryLabel>PO amount</SummaryLabel>
            <SummaryValue>
              &#8377; {po.po_amount?.toLocaleString("en-IN")}
            </SummaryValue>
          </SummaryText>
        </SummaryItem>

        <SummaryItem style={{ cursor: "pointer" }} onClick={downloadDeliveryInvoices}>
          <SummaryIcon>
            <Download size={20} />
          </SummaryIcon>
          <SummaryText>
            <SummaryLabel
              as="button"
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                color: "#ef4f5f",
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Download delivery documents
            </SummaryLabel>
          </SummaryText>
        </SummaryItem>
      </DetailsSummaryCard>

      {/* ── EWB / Upload docs buttons ── */}
      {shouldShowEwbButton?.() && (
        <Actions style={{ justifyContent: "flex-start" }}>
          <ActionButton type="button" onClick={handleGenerateEwb}>
            Generate E-Way bill
          </ActionButton>
          <ActionButton
            type="button"
            onClick={() => navigate(`/ddPo/uploadDocs/${purchaseOrderId}`)}
          >
            Upload delivery documents
          </ActionButton>
        </Actions>
      )}

      {/* ── Products table ── */}
      <ProductsCard>
        <ProductsTitle>{strings.productDetails}</ProductsTitle>
        <Table
          columns={columns}
          data={items || []}
          getRowId={(row, index) =>
            String(row.id ?? row.product_number ?? index)
          }
          emptyMessage="No products found"
        />
      </ProductsCard>
    </DesktopPage>
  );
}
